import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { Box, Typography, Stack, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';

import WaveformDisplay from './WaveformDisplay';
import PlaybackControls from './PlaybackControls';
import ZoomControls from './ZoomControls';
import CropInfo from './CropInfo';
import {
    getWaveSurferConfig,
    getThemeColors,
    getBoxShadow,
    REGION_COLOR,
    MIN_REGION_LENGTH,
    ZOOM_STEP,
    MIN_ZOOM,
    MAX_ZOOM,
    LARGE_FILE_THRESHOLD
} from './audioUtils';
import './AudioPlayer.css';

const AudioPlayer = memo(({ audioUrl, file, onTimeUpdate, onCropChange, cropStart, cropEnd, duration }) => {
    const theme = useTheme();
    const isDarkMode = useMemo(() => theme.palette.mode === 'dark', [theme.palette.mode]);
    
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [zoom, setZoom] = useState(MIN_ZOOM);
    
    // State for large file processing
    const [isProcessingPeaks, setIsProcessingPeaks] = useState(false);
    const [peakProgress, setPeakProgress] = useState(0);
    const [peakStatus, setPeakStatus] = useState('');
    const [precomputedPeaks, setPrecomputedPeaks] = useState(null);
    const [precomputedDuration, setPrecomputedDuration] = useState(null);
    
    const waveformRef = useRef(null);
    const wavesurferRef = useRef(null);
    const regionsPluginRef = useRef(null);
    const cropRegionRef = useRef(null);
    const isUpdatingRegionRef = useRef(false);
    const isMountedRef = useRef(true);
    const peakWorkerRef = useRef(null);
    
    // Check if this is a large file
    const isLargeFile = useMemo(() => {
        return file && file.size > LARGE_FILE_THRESHOLD;
    }, [file]);
    
    // Store callbacks in refs to avoid recreating WaveSurfer on callback changes
    const onCropChangeRef = useRef(onCropChange);
    const onTimeUpdateRef = useRef(onTimeUpdate);
    
    useEffect(() => {
        onCropChangeRef.current = onCropChange;
        onTimeUpdateRef.current = onTimeUpdate;
    }, [onCropChange, onTimeUpdate]);
    
    // Process large files with Web Worker to extract peaks
    useEffect(() => {
        if (!isLargeFile || !file || !duration) {
            setPrecomputedPeaks(null);
            setPrecomputedDuration(null);
            return;
        }
        
        setIsProcessingPeaks(true);
        setPeakProgress(0);
        setPeakStatus('Initializing...');
        
        // Create Web Worker
        const worker = new Worker(
            new URL('./peakWorker.js', import.meta.url),
            { type: 'module' }
        );
        peakWorkerRef.current = worker;
        
        worker.onmessage = (event) => {
            const { type, progress, status, peaks, duration: audioDuration, error } = event.data;
            
            if (!isMountedRef.current) return;
            
            switch (type) {
                case 'progress':
                    setPeakProgress(progress);
                    setPeakStatus(status);
                    break;
                case 'complete':
                    setPrecomputedPeaks(peaks);
                    setPrecomputedDuration(audioDuration);
                    setIsProcessingPeaks(false);
                    setPeakProgress(100);
                    setPeakStatus('Complete');
                    break;
                case 'error':
                    console.error('Peak extraction error:', error);
                    setIsProcessingPeaks(false);
                    setPeakStatus(`Error: ${error}`);
                    // Fall back to default behavior (may still crash for very large files)
                    setPrecomputedPeaks(null);
                    setPrecomputedDuration(null);
                    break;
                default:
                    break;
            }
        };
        
        worker.onerror = (error) => {
            console.error('Worker error:', error);
            if (isMountedRef.current) {
                setIsProcessingPeaks(false);
                setPeakStatus('Worker error');
            }
        };
        
        // Start processing - pass duration since we can't decode in the worker
        worker.postMessage({ file, duration });
        
        return () => {
            worker.terminate();
            peakWorkerRef.current = null;
        };
    }, [file, isLargeFile, duration]);

    // Handle unhandled promise rejections from WaveSurfer (AbortError during cleanup)
    useEffect(() => {
        const handleUnhandledRejection = (event) => {
            if (event.reason?.name === 'AbortError' && event.reason?.message?.includes('aborted')) {
                // Suppress AbortError from WaveSurfer cleanup
                event.preventDefault();
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    // Memoize configurations - pass isLargeFile to avoid WebAudio backend for large files
    const wavesurferConfig = useMemo(() => getWaveSurferConfig(isDarkMode, isLargeFile), [isDarkMode, isLargeFile]);
    const colors = useMemo(() => getThemeColors(isDarkMode), [isDarkMode]);
    const boxShadow = useMemo(() => getBoxShadow(isDarkMode), [isDarkMode]);

    // Initialize WaveSurfer
    useEffect(() => {
        if (!waveformRef.current || !audioUrl) return;
        
        // For large files, wait until peaks are computed
        if (isLargeFile && isProcessingPeaks) return;
        if (isLargeFile && !precomputedPeaks) return;

        isMountedRef.current = true;
        setIsLoading(true);

        // Create regions plugin
        const regionsPlugin = RegionsPlugin.create();
        regionsPluginRef.current = regionsPlugin;

        // Create WaveSurfer instance
        const wavesurfer = WaveSurfer.create({
            container: waveformRef.current,
            ...wavesurferConfig,
            plugins: [regionsPlugin]
        });

        wavesurferRef.current = wavesurfer;

        // Load audio - use pre-computed peaks for large files to avoid memory issues
        if (isLargeFile && precomputedPeaks && precomputedDuration) {
            // Pass peaks and duration to skip decoding
            wavesurfer.load(audioUrl, precomputedPeaks, precomputedDuration);
        } else {
            // Default behavior for small files
            wavesurfer.load(audioUrl);
        }

        // Event listeners
        wavesurfer.on('ready', () => {
            if (!isMountedRef.current) return;
            
            setIsLoading(false);
            const audioDuration = wavesurfer.getDuration();
            
            // Create initial crop region (full audio by default)
            const startTime = cropStart || 0;
            const endTime = cropEnd || audioDuration;
            
            // Safely remove old region if it exists
            if (cropRegionRef.current) {
                try {
                    cropRegionRef.current.remove();
                } catch (error) {
                    // Region might have already been removed during cleanup
                    console.debug('Region already removed:', error);
                }
                cropRegionRef.current = null;
            }
            
            cropRegionRef.current = regionsPlugin.addRegion({
                start: startTime,
                end: endTime,
                color: REGION_COLOR,
                drag: false,
                resize: true,
                minLength: MIN_REGION_LENGTH,
                maxLength: audioDuration
            });

            // Handle region updates
            cropRegionRef.current.on('update-end', () => {
                if (!isUpdatingRegionRef.current && isMountedRef.current) {
                    const region = cropRegionRef.current;
                    if (region && onCropChangeRef.current) {
                        onCropChangeRef.current(region.start, region.end);
                    }
                }
            });
        });

        wavesurfer.on('audioprocess', (time) => {
            if (isMountedRef.current) {
                setCurrentTime(time);
                if (onTimeUpdateRef.current) {
                    onTimeUpdateRef.current(time);
                }
            }
        });

        wavesurfer.on('play', () => {
            if (isMountedRef.current) setIsPlaying(true);
        });

        wavesurfer.on('pause', () => {
            if (isMountedRef.current) setIsPlaying(false);
        });

        wavesurfer.on('finish', () => {
            if (isMountedRef.current) setIsPlaying(false);
        });

        // Cleanup
        return () => {
            isMountedRef.current = false;
            
            // Clean up region reference first
            if (cropRegionRef.current) {
                try {
                    cropRegionRef.current.remove();
                } catch (error) {
                    // Region might have already been removed
                    console.debug('Region cleanup:', error);
                }
                cropRegionRef.current = null;
            }
            
            if (wavesurfer) {
                // Unsubscribe and destroy
                wavesurfer.unAll();
                // Use setTimeout to avoid AbortError in React StrictMode
                setTimeout(() => {
                    try {
                        if (wavesurfer && typeof wavesurfer.destroy === 'function') {
                            wavesurfer.destroy();
                        }
                    } catch (error) {
                        // Suppress abort errors during cleanup - this is expected in React StrictMode
                        if (!error.message?.includes('aborted')) {
                            console.error('Error destroying wavesurfer:', error);
                        }
                    }
                }, 0);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioUrl, wavesurferConfig, isLargeFile, isProcessingPeaks, precomputedPeaks, precomputedDuration]);

    // Update region when crop values change externally
    useEffect(() => {
        if (cropRegionRef.current && wavesurferRef.current && !isLoading && isMountedRef.current) {
            isUpdatingRegionRef.current = true;
            const region = cropRegionRef.current;
            
            try {
                if (region.start !== cropStart || region.end !== cropEnd) {
                    region.setOptions({
                        start: cropStart,
                        end: cropEnd
                    });
                }
            } catch (error) {
                console.debug('Error updating region:', error);
            } finally {
                isUpdatingRegionRef.current = false;
            }
        }
    }, [cropStart, cropEnd, isLoading]);

    // Handle play/pause
    const handlePlayPause = useCallback(() => {
        if (wavesurferRef.current) {
            wavesurferRef.current.playPause();
        }
    }, []);

    // Handle zoom with optimized state updates
    const handleZoomIn = useCallback(() => {
        setZoom(prev => {
            const newZoom = Math.min(prev + ZOOM_STEP, MAX_ZOOM);
            if (wavesurferRef.current) {
                wavesurferRef.current.zoom(newZoom);
            }
            return newZoom;
        });
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoom(prev => {
            const newZoom = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
            if (wavesurferRef.current) {
                wavesurferRef.current.zoom(newZoom);
            }
            return newZoom;
        });
    }, []);

    // Handle reset crop
    const handleResetCrop = useCallback(() => {
        if (wavesurferRef.current && cropRegionRef.current && onCropChangeRef.current) {
            const audioDuration = wavesurferRef.current.getDuration();
            cropRegionRef.current.setOptions({
                start: 0,
                end: audioDuration
            });
            onCropChangeRef.current(0, audioDuration);
        }
    }, []);

    return (
        <Box 
            sx={{ 
                width: '100%', 
                mt: 2, 
                bgcolor: colors.bgPrimary,
                p: 3,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                boxShadow
            }}
        >
            {/* Header with title */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        color: colors.text,
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem'
                    }}
                >
                    Audio Trimmer
                </Typography>
            </Stack>

            {/* Peak Processing Progress for Large Files */}
            {isLargeFile && isProcessingPeaks && (
                <Box sx={{ mb: 2 }}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: colors.text,
                            display: 'block',
                            mb: 1
                        }}
                    >
                        {peakStatus || 'Analyzing audio...'}
                    </Typography>
                    <LinearProgress 
                        variant="determinate" 
                        value={peakProgress} 
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: colors.bgSecondary,
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: colors.buttonBg,
                                borderRadius: 3
                            }
                        }}
                    />
                </Box>
            )}

            {/* Waveform Container */}
            <WaveformDisplay 
                waveformRef={waveformRef}
                isLoading={isLoading || (isLargeFile && isProcessingPeaks)}
                colors={colors}
            />

            {/* Controls Section */}
            <Stack spacing={2}>
                {/* Playback and Zoom Controls */}
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <PlaybackControls
                        isPlaying={isPlaying}
                        isLoading={isLoading}
                        currentTime={currentTime}
                        duration={duration}
                        onPlayPause={handlePlayPause}
                        colors={colors}
                    />

                    <ZoomControls
                        isLoading={isLoading}
                        zoom={zoom}
                        onZoomIn={handleZoomIn}
                        onZoomOut={handleZoomOut}
                        onReset={handleResetCrop}
                        colors={colors}
                    />
                </Stack>

                {/* Crop Selection Info */}
                <CropInfo
                    cropStart={cropStart}
                    cropEnd={cropEnd}
                    colors={colors}
                />

                {/* Instructions */}
                <Typography 
                    variant="caption" 
                    sx={{ 
                        color: colors.textSecondary,
                        fontStyle: 'italic',
                        textAlign: 'center',
                        display: 'block'
                    }}
                >
                    Drag the yellow handles on the waveform to trim your audio
                </Typography>
            </Stack>
        </Box>
    );
});

AudioPlayer.displayName = 'AudioPlayer';

export default AudioPlayer;

